import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';

/** Crowd-control / movement-control and elemental-status applier blows (stun, freeze, bind, fear, ...). */
export const cc: Record<number, IBlowDef> = {
    41: {
        name: 'Ice Stack',
        wikiDesc: 'Rate% chance to freeze the target. Durability is the number of hits the freeze can ' +
            'absorb before it shatters (each landed hit decrements it by 1; reaching 0 breaks the ' +
            'freeze early). -1 = infinite durability.',
        describe({ params }) {
            const nums = params.numbers();
            const rate = nums[0];
            if (isNaN(rate)) {
                return null;
            }
            const durability = nums[1];
            const out: Segment[] = [
                'Freezes the target at a ', seg.pct(rate, 2, params.span(0) || undefined), ' chance',
            ];
            if (durability === -1) {
                out.push(
                    ', with ', seg.val('infinite', params.span(1) || undefined), ' durability',
                );
            } else if (!isNaN(durability)) {
                out.push(
                    ', absorbing ', seg.num(durability, params.span(1) || undefined),
                    ' hits before shattering',
                );
            }
            return out;
        },
    },
    43: {
        name: 'Electrocute',
        wikiDesc: 'Electrocutes at Rate% chance, reducing light resist by LightResistReduction%.',
        describe({ params }) {
            const f = params.numbers();
            const rate = f[0];
            if (isNaN(rate)) {
                return null;
            }
            const out: Segment[] = ['Electrocutes the target at a ', seg.pct(rate), ' chance'];
            const reduction = f[1];
            if (reduction) {
                const dir = reduction < 0 ? 'reduced' : 'increased';
                out.push(`, light resist ${dir} by `, seg.pct(Math.abs(reduction)));
            }
            return out;
        },
    },
    45: {
        name: 'Sleep',
        wikiDesc: "Puts the target to sleep. Chance is reduced by the target's dark element resist.",
        describe({ params }) {
            const chance = params.nextNum();
            if (isNaN(chance)) {
                return null;
            }
            return ['Puts the target to sleep at a ', seg.pct(chance), ' chance'];
        },
    },
    63: {
        name: 'Petrification',
        wikiDesc: 'Petrifies the actor, optionally granting it HP required to break it out and dealing HP ' +
            'upon being broken out early (but not on natural expiry). The target takes no damage to real ' +
            'HP while petrified unless HPToBreakOut is negative.',
        describe({ params }) {
            const f = params.numbers();
            const breakoutHp = f[0];
            if (isNaN(breakoutHp)) {
                return null;
            }
            const out: Segment[] = ['Petrifies the target'];
            if (breakoutHp < 0) {
                out.push(', taking real damage while petrified');
            } else if (breakoutHp > 0) {
                out.push(', requiring ', seg.num(breakoutHp), ' HP to break out early');
                const breakoutDmg = f[1];
                if (breakoutDmg) {
                    out.push(' (dealing ', seg.num(breakoutDmg), ' HP on breakout)');
                }
            }
            return out;
        },
    },
    67: {
        name: 'Disable Active Skills',
        wikiDesc: 'Disables the use of any active skill.',
    },
    70: {
        name: 'Immobilize',
        wikiDesc: 'Unable to move. If 1, only disallows horizontal movement.',
        describe({ params }) {
            const v = params.nextNum();
            if (isNaN(v)) {
                return null;
            }
            return v === 1 ? ['Unable to move horizontally'] : ['Unable to move'];
        },
    },
    71: {
        name: 'Unable to act',
        wikiDesc: "The actor is unable to perform any action that has an 'Attack' state block.",
    },
    73: {
        name: 'Transparency',
        wikiDesc: 'Makes the actor transparent. InvisibleType 0 sets a fixed 50% transparency; type 1 ' +
            'requires all the alpha values to be set explicitly.',
        describe({ params }) {
            const f = params.numbers();
            const type = f[0];
            if (isNaN(type)) {
                return null;
            }
            const TYPES = ['fixed 50%', 'custom alpha'];
            const out: Segment[] = ['Makes the actor transparent (', seg.en(type, TYPES, 'unknown'), ')'];
            if (type === 1 && f.length >= 5 && !f.slice(1, 5).some(isNaN)) {
                out.push(': party idle ', seg.pct(f[1]), ', party attack ', seg.pct(f[2]),
                    ', enemy idle ', seg.pct(f[3]), ', enemy attack ', seg.pct(f[4]));
                if (!isNaN(f[5])) {
                    out.push(', force-start ', seg.pct(f[5]));
                }
            }
            return out;
        },
    },
    77: {
        name: 'Immunity to specific blow',
        wikiDesc: 'Grants immunity to the listed state effects, optionally at a limited rate. A 4th ' +
            'segment of 1 also cleanses matching state effects already applied when the immunity ' +
            'begins, and persistent (SoO artifact) buffs stay suppressed while it is active. The 3rd ' +
            'segment (duration reduction) is parsed by the game but currently non-functional.',
        describe({ raw }) {
            const parts = raw.split('|');
            const ids = ParamReader.numList(parts[0], ';');
            if (!ids.length) {
                return null;
            }
            const out: Segment[] = ['Grants immunity to ',
                seg.list(ids.map((id) => [seg.blow(id)] as Segment[]))];
            if (parts.length >= 2) {
                const rate = ParamReader.num(parts[1]);
                if (!isNaN(rate)) {
                    out.push(' at a ', seg.pct(rate / 100), ' rate');
                }
            }
            // parts[2] (duration reduction) is parsed by the game but currently non-functional
            if (parts.length >= 4 && ParamReader.num(parts[3]) > 0) {
                out.push(', cleansing matching effects already on the actor');
            }
            return out;
        },
    },
    78: {
        name: 'Silenced',
        wikiDesc: 'Unable to use skills. Leaving the parameter empty only silences skills that require ' +
            'mana; passing 1 silences all skills.',
        describe({ raw }) {
            const trimmed = raw.trim();
            if (trimmed === '') {
                return ['Unable to use skills that require mana'];
            }
            const v = Number(trimmed);
            if (isNaN(v)) {
                return null;
            }
            return v === 1 ? ['Unable to use any skill'] : ['Unable to use skills that require mana'];
        },
    },
    84: {
        name: 'Force action',
        wikiDesc: 'Forces the actor to be unable to act. For type 0, the effect immediately ends ' +
            '(regardless of its SE duration) and knocks the actor down for 3s. Other types simply disable ' +
            'actions for the duration.',
        describe({ params }) {
            const type = params.nextNum();
            if (isNaN(type)) {
                return null;
            }
            const out: Segment[] = ['Forces the actor to be unable to act (type ', seg.id(type), ')'];
            if (type === 0) {
                out.push(', ending immediately and knocking the actor down for ', seg.secs(3000));
            }
            return out;
        },
    },
    86: {
        name: 'Team reversal',
        wikiDesc: 'Switches teams. Type 0 remaps ACT hit-block damage types (Enemy<->Friend, others ' +
            'unchanged), type 1 sets the team to the opposite side (players to team 1, monsters to team ' +
            '0), type 2 sets all ACT hit blocks to affect everyone.',
        describe({ params }) {
            const type = params.nextNum();
            if (isNaN(type)) {
                return null;
            }
            const TYPES = [
                'swaps enemy/friend hit blocks',
                'sets team to the opposite side',
                'sets hit blocks to affect all targets',
            ];
            return ['Team reversal: ', seg.en(type, TYPES, 'unknown type')];
        },
    },
    99: {
        name: 'Invincibility',
        wikiDesc: "Invincible. Does not ignore the 'ignore iframe' checker on hit blocks (i.e. attacks " +
            'with that flag can still land).',
        describe({ durationMs }) {
            if (!durationMs) {
                return null;
            }
            return ['Invincible'];
        },
    },
    130: {
        name: 'Post-stage clear effect',
        wikiDesc: 'Cannot attack or use skills; the current move/attack action is cancelled to Stand. ' +
            'Currently only used when a stage clear score comes out.',
    },
    131: {
        name: 'Weight',
        wikiDesc: "Sets the actor's weight to an absolute value for the duration (not a delta). Weight " +
            'divides knockback velocity; weight > 1 heavily damps horizontal knockback, 0 = can never be ' +
            'launched airborne.',
        describe({ params }) {
            const weight = params.nextNum();
            if (isNaN(weight)) {
                return null;
            }
            return ['Sets weight to ', seg.num(weight)];
        },
    },
    133: {
        name: 'Forcibly stun',
        wikiDesc: "Value is multiplied into a hitblock's damage coefficient to determine stun chance " +
            "(e.g. Value 1.0 with a hitblock's 30% damage coefficient == 30% stun chance).",
        describe({ params }) {
            const value = params.nextNum();
            if (isNaN(value)) {
                return null;
            }
            return ['Forces a stun chance equal to ', seg.pct(value), ' × the hitblock damage coefficient'];
        },
    },
    138: {
        name: 'Ignore CanBeHit status',
        wikiDesc: 'Ignores hittable state, except if invincible due to State Effect 99.',
        describe() {
            return ['Ignores hittable state, except invincibility granted by ', seg.blow(99)];
        },
    },
    146: {
        name: 'Time stop',
        wikiDesc: 'Action frame rate is set to 0 (full time stop). Highest priority out of all ' +
            'time-related state effects.',
        describe({ params }) {
            const mech = params.nextNum();
            const out: Segment[] = ['Time is stopped'];
            if (!isNaN(mech) && mech !== 0) {
                out.push(' with A-D mech active');
            }
            return out;
        },
    },
    149: {
        name: 'Ice prison',
        wikiDesc: "Traps the target: can't move or act, frame-stopped, weight 0, moved to the caster's " +
            'team. Takes no real damage while trapped; hits drain a durability pool instead (shown as a ' +
            'gauge). Pool emptied = breaks early with no damage; natural expiry = takes FinalDamage. ' +
            'Also ticks IntervalDamage every 1s.',
        describe({ params }) {
            const f = params.numbers();
            const durability = f[0];
            const finalDamage = f[1];
            const intervalDamage = f[2] || 0;
            if (isNaN(durability) || isNaN(finalDamage)) {
                return null;
            }
            const DURABILITY_TYPES = ["hits' SA break value ×100", "hits' raw damage"];
            const durabilityType = isNaN(f[3]) ? 0 : f[3];
            const durabilityTypeSpan = isNaN(f[3]) ? undefined : params.span(3) || undefined;
            const out: Segment[] = [
                'Traps the target with ', seg.num(durability, params.span(0) || undefined),
                ' durability (drained by ',
                seg.en(durabilityType, DURABILITY_TYPES, 'unknown', durabilityTypeSpan),
                '); breaking the pool early deals no damage, ' +
                'natural expiry deals ', seg.num(finalDamage, params.span(1) || undefined),
            ];
            if (intervalDamage) {
                out.push(', ticking ', seg.num(intervalDamage, params.span(2) || undefined), ' damage every 1s');
            }
            return out;
        },
    },
    150: {
        name: 'Immune to State Effect',
        wikiDesc: 'Grants immunity to all state effects except the listed ones (SE1, SE2, ...). Shows ' +
            "'Immune' text if ImmuneResist is set. If Inverse is set, grants immunity only to the listed " +
            'state effects instead. Flags are appended to the last id with an underscore, e.g. ' +
            '`SE1;SE2;SE3_1_1`.',
        describe({ raw }) {
            const fields = raw.split(';').map((s) => s.trim()).filter((s) => s !== '');
            if (!fields.length || fields[0] === '') {
                return null;
            }
            const lastParts = fields[fields.length - 1].split('_');
            fields[fields.length - 1] = lastParts[0];
            const immuneResist = lastParts[1] === '1';
            const inverse = lastParts[2] === '1';
            const ids = fields.filter((s) => s !== '').map(Number).filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            const out: Segment[] = [
                inverse ? 'Grants immunity only to ' : 'Grants immunity to all state effects except ',
                seg.list(ids.map((id) => [seg.blow(id)] as Segment[])),
            ];
            if (immuneResist) {
                out.push(', showing "Immune" text');
            }
            return out;
        },
    },
    154: {
        name: 'Grant immunity to skill',
        wikiDesc: 'Grants immunity to the listed skill IDs, using OnHitType as the resist-processor ' +
            'selector.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const onHitType = ParamReader.num(f[f.length - 1]);
            const skillIds = f.slice(0, f.length - 1).map(ParamReader.num).filter((v) => !isNaN(v));
            if (!skillIds.length) {
                return null;
            }
            const out: Segment[] = ['Grants immunity to ',
                seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[]))];
            if (!isNaN(onHitType)) {
                out.push(' (on-hit type ', seg.id(onHitType), ')');
            }
            return out;
        },
    },
    204: {
        name: 'Force Out Blow',
        wikiDesc: 'Hits forcibly apply their knockback physics to targets whose weight is <= Weight, ' +
            'shoving even targets that would not normally react.',
        describe({ params }) {
            const weight = params.nextNum();
            if (isNaN(weight)) {
                return null;
            }
            return [
                'Hits forcibly apply knockback physics to targets with weight ≤ ', seg.num(weight),
                ' (even ones that would not normally react)',
            ];
        },
    },
    206: {
        name: 'ActionChange%',
        wikiDesc: 'When applied, Chance to force the target into ActionName (e.g. the wax slip). Fires ' +
            'once at apply time, not per hit.',
        describe({ params }) {
            const f = params.fields();
            const chance = ParamReader.num(f[0]);
            const action = f[1];
            if (isNaN(chance) || !action) {
                return null;
            }
            return [
                'On apply (once, not per hit), at a ', seg.pct(chance), ' chance, forces the target into ',
                seg.action(action),
            ];
        },
    },
    218: {
        name: 'Binding',
        wikiDesc: 'Binds at a rate of Rate% with a breakout durability of Durability. The bound target ' +
            'performs Action, and can break out via BreakoutType (1 = mashing A-D, 2 = mashing spacebar).',
        note: 'Field 5 is Timestopped with 5 params, or BreakoutType with more; Rate% is unused.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 3 || !f[2]) {
                return null;
            }
            const escapeable = f[1] === '1';
            const action = f[2];
            let breakoutType = NaN;
            let timeStopped = false;
            if (f.length === 5) {
                timeStopped = f[4] === '1';
            } else if (f.length > 5) {
                breakoutType = ParamReader.num(f[4]);
            }
            const out: Segment[] = [
                'Binds the target, performing ', seg.action(action, params.span(2) || undefined),
            ];
            if (escapeable) {
                if (!isNaN(breakoutType)) {
                    const BREAKOUT_TYPES = ['(type 0)', 'mashing A-D', 'mashing spacebar'];
                    out.push('; can break free by ', seg.en(
                        breakoutType, BREAKOUT_TYPES, 'unknown method', params.span(4) || undefined));
                } else {
                    out.push('; can break free');
                }
            } else {
                out.push('; cannot break free');
            }
            if (timeStopped) {
                out.push(' (time is stopped while bound)');
            }
            if (f[3] === '0') {
                out.push(', respecting ice resistance');
            }
            if (!isNaN(breakoutType)) {
                const speed = ParamReader.num(f[5]);
                const leniency = ParamReader.num(f[6]);
                const size = ParamReader.num(f[7]);
                if (!isNaN(speed)) {
                    out.push(', speed ', seg.num(speed, params.span(5) || undefined));
                }
                if (!isNaN(leniency)) {
                    out.push(', leniency ', seg.pct(leniency, 2, params.span(6) || undefined));
                }
                if (!isNaN(size)) {
                    out.push(', size ', seg.num(size, params.span(7) || undefined));
                }
            }
            return out;
        },
    },
    224: {
        name: 'Prevent action',
        wikiDesc: 'Blocks the listed actions by exact action-name match.',
        describe({ params }) {
            const actions = params.fields().filter((a) => a !== '');
            if (!actions.length) {
                return null;
            }
            return ['Blocks the actions ', seg.list(actions.map((a) => [seg.action(a)] as Segment[]))];
        },
    },
    225: {
        name: 'Confusion',
        wikiDesc: 'Controls are reversed (client input inversion).',
        describe({ params }) {
            const mode = params.nextNum();
            if (isNaN(mode)) {
                return null;
            }
            const MODES = ['(none)', 'keyboard + mouse', 'keyboard only', 'mouse only'];
            return ['Controls are reversed: ', seg.en(mode, MODES, 'unknown mode')];
        },
    },
    228: {
        name: 'Ignore Hit Signal',
        wikiDesc: 'Accurately hit enemies become drunk and their attacks miss at a certain probability.',
        describe({ params }) {
            const prob = params.nextNum();
            if (isNaN(prob)) {
                return null;
            }
            return [
                'Accurately hit enemies become drunk, causing their attacks to miss at a ', seg.pct(prob),
                ' chance',
            ];
        },
    },
    230: {
        name: 'Spectator state',
        wikiDesc: 'Puts a player into spectator state: current skill cancelled, battle mode forced off, ' +
            'MP-consuming skills blocked. PvP All-Kill mode rooms only, no-op elsewhere.',
        describe() {
            return [
                'Puts the player into spectator state: cancels the current skill, forces battle mode ' +
                'off, and blocks MP-consuming skills (PvP All-Kill mode rooms only; no-op elsewhere)',
            ];
        },
    },
    233: {
        name: 'Guaranteed kill',
        wikiDesc: 'Kills the target. KillFilter 0 = summons?, 1 = players?',
        describe({ params }) {
            const filterVal = params.nextNum();
            const out: Segment[] = ['Guarantees a kill'];
            if (!isNaN(filterVal)) {
                const FILTER_TYPES = ['summons (?)', 'players (?)'];
                out.push(' (KillFilter ', seg.en(filterVal, FILTER_TYPES, 'unknown'), ')');
            }
            return out;
        },
    },
    235: {
        name: 'Avoid Debuff Action',
        wikiDesc: 'Freeze (41), Frostbite (144), Electrocute/Shock (43), and Bind (218) apply only ' +
            'their stat effects, without the action penalty (nest boss style). Also strips the penalty ' +
            'from instances already active when applied. Burn is NOT covered despite the original tooltip.',
        describe() {
            return [
                seg.blow(41), ', ', seg.blow(144), ', ', seg.blow(43), ', and ', seg.blow(218),
                ' apply only their stat effects, without the action penalty (also strips the penalty ' +
                'from already-active instances). ', seg.blow(42), ' is NOT covered',
            ];
        },
    },
    237: {
        name: 'Disarm',
        wikiDesc: "Forcibly unequips the target's main weapon and locks weapon changing for the " +
            'duration (restored when it ends); players also drop out of battle mode.',
        describe() {
            return [
                "Forcibly unequips the target's main weapon and locks weapon changing for the duration " +
                '(restored when it ends); players also drop out of battle mode',
            ];
        },
    },
    238: {
        name: 'Change Weapon',
        wikiDesc: '(Monster only) Attaches a weapon created from itemtable WeaponID into the given ' +
            "equip slot for the duration. Won't begin while disarmed (237).",
        describe({ params }) {
            const f = params.numbers();
            const slot = f[0];
            const weaponId = f[1];
            if (isNaN(slot) || isNaN(weaponId)) {
                return null;
            }
            return [
                '(Monster only) Equips item ', seg.id(weaponId), ' as a weapon in slot ', seg.id(slot),
                ' (0 = main); does not begin while disarmed (', seg.blow(237), ')',
            ];
        },
    },
    239: {
        name: 'Conditionally Invincible',
        wikiDesc: 'Makes this actor invincible to all attacks except from actors who have SE 240 with ' +
            'the correct InvulnID.',
        describe({ params }) {
            const invulnId = params.nextNum();
            if (isNaN(invulnId)) {
                return null;
            }
            return [
                'Invincible to all attacks except from actors with ', seg.blow(240), ' matching ID ',
                seg.id(invulnId),
            ];
        },
    },
    240: {
        name: 'Satisfy Conditional Invincibility',
        wikiDesc: 'Allows the actor with this SE to attack actors with SE 239 with the same InvulnID.',
        describe({ params }) {
            const invulnId = params.nextNum();
            if (isNaN(invulnId)) {
                return null;
            }
            return ['Allows attacking actors with ', seg.blow(239), ' matching ID ', seg.id(invulnId)];
        },
    },
    241: {
        name: 'Phalaenopsis (Screen Flash)',
        wikiDesc: "Blinds the player's screen. `0` for a white flash, `0;0;0;0;` for a black flash " +
            '(exact field meanings beyond arity are uncertain).',
        describe({ raw }) {
            const tokens = raw.split(';').map((s) => s.trim()).filter((s) => s !== '');
            if (!tokens.length) {
                return null;
            }
            return tokens.length <= 1
                ? ['Blinds the screen with a white flash']
                : ['Blinds the screen with a black flash'];
        },
    },
    252: {
        name: 'Grant immunity',
        wikiDesc: 'Grants immunity to the effects applied by SkillID1, SkillID2, SkillID3, ...',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            return [
                'Grants immunity to the effects of ',
                seg.list(ids.map((id) => [seg.skill(id)] as Segment[])),
            ];
        },
    },
    273: {
        name: 'Ignore suction',
        wikiDesc: 'Immune to Gravity (suction/pull) signals from actions and projectiles.',
        describe() {
            return ['Immune to gravity (suction/pull) signals from actions and projectiles'];
        },
    },
    175: {
        name: 'Push',
        wikiDesc: 'Forcibly knocks back (0) or lightly pushes (1) with fixed physics, 100% stagger ' +
            'and 0 damage. TriggerType 0 fires on attackers when the holder is hit; 1 fires on targets ' +
            'the holder hits.',
        describe({ params }) {
            const f = params.numbers();
            const pushType = f[0];
            const triggerType = f[1];
            if (isNaN(pushType) || isNaN(triggerType)) {
                return null;
            }
            const PUSH_TYPES = ['knockback', 'push'];
            const TRIGGER_TYPES = ['on being hit', 'on hitting'];
            return [
                seg.en(pushType, PUSH_TYPES, 'unknown'), ' the target ',
                seg.en(triggerType, TRIGGER_TYPES, 'unknown'),
                ' (fixed physics, 100% stagger, 0 damage)',
            ];
        },
    },
    305: {
        name: 'Fear',
        wikiDesc: "Victim can't act and is forced to run directly away from the caster about once per " +
            'second (Hit_Horror action). Players can escape early by mashing left/right (or the ' +
            'metronome minigame), each success removing ShakeReduceTime from the remaining duration.',
        note: 'All 6 fields are needed for the metronome form, e.g. `100;1000` or `100;1000;1;5;1;20`.',
        describe({ params }) {
            const f = params.numbers();
            const probability = f[0];
            const shakeReduceMs = f[1];
            if (isNaN(probability) || isNaN(shakeReduceMs)) {
                return null;
            }
            const out: Segment[] = [
                'Forces the target to run directly away from the caster (about once per second), at a ',
                seg.pct(probability / 100, 2, params.span(0) || undefined), ' chance',
            ];
            if (shakeReduceMs > 0) {
                out.push(
                    '; can be escaped early by mashing left/right, each success removing ',
                    seg.secs(shakeReduceMs, params.span(1) || undefined), ' from the remaining duration',
                );
            } else {
                out.push(' (no escape UI)');
            }
            if (f.length >= 6 && f[2] === 1) {
                const velocity = f[3];
                const round = f[4];
                const barSize = f[5];
                out.push(
                    ' via the metronome minigame (velocity ', seg.num(velocity, params.span(3) || undefined),
                    ', ', round === 1 ? 'round' : 'not round', ', bar size ',
                    seg.num(barSize, params.span(5) || undefined), ')',
                );
            }
            return out;
        },
    },
    311: {
        name: 'Immune to state effects except guaranteed kill',
        wikiDesc: 'Rejects every incoming state effect whose applier is not the holder itself, except ' +
            'guaranteed kill (233). Self-applied effects still work; this does not block damage.',
        describe() {
            return [
                'Rejects every incoming state effect not applied by the holder itself, except ',
                seg.blow(233), ' (self-applied effects still work; does not block damage)',
            ];
        },
    },
    312: {
        name: 'True invincibility',
        wikiDesc: 'Higher invincibility than 99. Cannot be hit at all (so no on-hit effects either) and ' +
            'immune to DoT/state-blow damage.',
        describe() {
            return ['Cannot be hit at all (no on-hit effects either) and immune to DoT/state-blow damage'];
        },
    },
    321: {
        name: 'Buff Ban',
        wikiDesc: 'Debuff: blocks incoming buff-type state effects (from non-passive skills with ' +
            'duration type Buff and Dissolvable 0) while applied; listed blow indices are exempt.',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            const out: Segment[] = ['Blocks incoming buff-type state effects while applied'];
            if (ids.length) {
                out.push(', except ', seg.list(ids.map((id) => [seg.blow(id)] as Segment[])));
            }
            return out;
        },
    },
    386: {
        name: 'Hypnosis',
        wikiDesc: 'You cannot move due to hypnosis. Movement speed is adjusted by ' +
            'MovementSpeedMod%. Escapable either by mashing A/D (reducing remaining time by ' +
            'ADReductionTime per success) or, if useSpacebar is set, via a spacebar-timing minigame.',
        describe({ params }) {
            const f = params.numbers();
            const chance = f[0];
            const adReductionMs = f[1];
            const moveSpeedMod = f[2];
            if (isNaN(chance) || isNaN(adReductionMs) || isNaN(moveSpeedMod)) {
                return null;
            }
            const dir = moveSpeedMod < 0 ? 'reduced' : 'increased';
            const out: Segment[] = [
                'At a ', seg.pct(chance, 2, params.span(0) || undefined),
                ' chance, hypnotizes the target (unable to move), with ' +
                `movement speed ${dir} by `, seg.pct(Math.abs(moveSpeedMod), 2, params.span(2) || undefined),
            ];
            const useSpacebar = f[3] === 1;
            if (useSpacebar && f.length >= 7 && !f.slice(4, 7).some(isNaN)) {
                const speed = f[4];
                const round = f[5];
                const size = f[6];
                out.push(
                    '; escapable via a spacebar-timing minigame (speed ',
                    seg.num(speed, params.span(4) || undefined), ', ',
                    round === 1 ? 'round' : 'not round', ' hit zone, size ',
                    seg.num(size, params.span(6) || undefined), ')',
                );
            } else {
                out.push(
                    '; escapable by mashing A/D, each success reducing the remaining time by ',
                    seg.secs(adReductionMs, params.span(1) || undefined),
                );
            }
            return out;
        },
    },
};
